import {GitHubAvatar} from '@github-ui/github-avatar'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {TrashIcon} from '@primer/octicons-react'
import {Button, Label, Link, RelativeTime, useConfirm} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useCallback} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {BUTTON_LABELS} from '../constants/button-labels'
import {ERRORS} from '../constants/errors'
import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import {commitDeleteUserContentEditMutation} from '../mutations/delete-user-content-edit-mutation'
import type {EditHistoryDialogHeaderFragment$key} from './__generated__/EditHistoryDialogHeaderFragment.graphql'
import styles from './EditHistoryDialogHeader.module.css'

export const EditHistoryDialogHeader = ({
  queryRef,
  userContentEditId,
}: {
  queryRef: EditHistoryDialogHeaderFragment$key
  userContentEditId: string
}) => {
  const data = useFragment<EditHistoryDialogHeaderFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment EditHistoryDialogHeaderFragment on UserContentEdit {
        editor {
          avatarUrl
          login
        }
        deletedAt
        editedAt
        newest
        firstEdit
        viewerCanDelete
      }
    `,
    queryRef,
  )

  const environment = useRelayEnvironment()
  const confirm = useConfirm()
  const {addToast} = useToastContext()
  const deleteRevision = useCallback(async () => {
    if (
      await confirm({
        title: LABELS.confirmations.deleteEditHistoryTitle,
        content: LABELS.confirmations.deleteEditHistoryContent,
        confirmButtonType: 'danger',
        confirmButtonContent: LABELS.confirmations.deleteEditHistoryConfirmButtonContent,
      })
    ) {
      commitDeleteUserContentEditMutation({
        environment,
        input: {id: userContentEditId},
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotDeleteEditRevision,
          })
        },
      })
    }
  }, [addToast, confirm, environment, userContentEditId])

  const hasTrailingElement = data.newest || data.deletedAt

  return (
    <div className={styles.EditHistoryDialogHeaderContainer}>
      {data.editor && (
        <ActorOperationDetails
          login={data.editor.login}
          avatarUrl={data.editor.avatarUrl}
          time={data.editedAt}
          details={data.firstEdit ? LABELS.editHistory.created : LABELS.editHistory.edited}
          trailingElement={
            hasTrailingElement ? (
              <Label className={styles.statusLabel}>
                {data.deletedAt ? LABELS.editHistory.deleted : LABELS.editHistory.mostRecent}
              </Label>
            ) : undefined
          }
        />
      )}
      {data.viewerCanDelete && !data.newest && !data.deletedAt && (
        <Button
          variant="danger"
          size="small"
          aria-label={LABELS.editHistory.deleteRevisionAriaLabel}
          onClick={deleteRevision}
          className={styles.deleteRevisionButton}
        >
          <Octicon icon={TrashIcon} /> {BUTTON_LABELS.deleteEditRevision}
        </Button>
      )}
    </div>
  )
}

type ActorOperationDetailsProps = {
  login?: string
  avatarUrl?: string
  details: string
  time?: string
  trailingElement?: React.ReactElement
  className?: string
}

export function ActorOperationDetails({
  login,
  avatarUrl,
  details,
  time,
  className,
  trailingElement,
}: ActorOperationDetailsProps) {
  const showUserAvatar = !!avatarUrl && !!login
  return (
    <div className={styles.actorDetailsWrapper}>
      <div className={className}>
        {showUserAvatar && (
          <>
            <GitHubAvatar src={avatarUrl} size={16} alt={`@${login}`} className={styles.userAvatar} />
            <Link href={`/${login}`} className={styles.usernameBold} aria-label={`View profile for @${login}`}>
              {login}
            </Link>
          </>
        )}
        {!showUserAvatar && <span>{login ?? VALUES.ghostUserLogin}</span>}
        <span> {details} </span>
        {time && <RelativeTime date={new Date(time)} />}
      </div>
      {trailingElement}
    </div>
  )
}
